import { useClinicalRecordLock } from '../../../../components/clinical-record-lock';
import * as React from 'react';
import {
  Typography,
  Box,
  Stack,
  Grid,
  Autocomplete,
  TextField,
  Checkbox,
  Button,
  Dialog,
  DialogTitle,
  Alert,
  FormHelperText,
} from '@mui/material';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import FormProvider from '../../../../components/hook-form/FormProvider';
import { IHandoverSchema } from '../../../../@types/handover';
import { IClinicalRecordSchema } from '../../../../@types/clinicalRecord.type';
import { RHFCheckbox, RHFTextField } from '../../../../components/hook-form';
import { Controller, useForm, useFormContext } from 'react-hook-form';
import { HandoverService } from '../../../../api/handover.service';
import { useNavigate, useParams } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import {
  getApiErrorMessage,
  notifyHandoverSaveResult,
} from '../../../../utils/util';
import { LoadingButton } from '@mui/lab';
import { useEffect, useState } from 'react';
import { PATH_DASHBOARD } from '../../../../routes/paths';
// import { DispensaryService } from "../../../../api/dispensary.service";
import { IDispenseRequestFromHandover } from '../../../../@types/dispense';
import { useAuthContext } from '../../../../auth/useAuthContext';
import { toUpperText } from '../../../../utils/uppercase';
import FinalSummary from '../Refraction/FinalSummary';
import { PreviousRecords } from '../Examination/PreviousRecords';
import { FormControlLabel } from '@mui/material';
import SignatureDialog from '../../../../components/signature/SignatureDialog';
import Iconify from '../../../../components/iconify';
import SignedOffBanner from '../../../../components/signed-off-banner';
import { ClinicalRecords } from '../../../../api/clinicalRecords.service';
import DirectTillDialog from '../../../../components/handover/DirectTillDialog';
import { readPrescription } from '../../../../components/claims/readPrescription';
import { buildTillFocusState } from '../../Till/tillNavShared';

const label = { inputProps: { 'aria-label': 'Checkbox demo' } };

const monthsData = [
  { id: 1, number: 1 },
  { id: 2, number: 2 },
  { id: 3, number: 3 },
  { id: 4, number: 4 },
  { id: 5, number: 5 },
  { id: 6, number: 6 },
  { id: 7, number: 7 },
  { id: 8, number: 8 },
  { id: 9, number: 9 },
  { id: 10, number: 10 },
  { id: 11, number: 11 },
  { id: 12, number: 12 },
  { id: 13, number: 13 },
  { id: 14, number: 14 },
  { id: 15, number: 15 },
  { id: 16, number: 16 },
  { id: 17, number: 17 },
  { id: 18, number: 18 },
  { id: 19, number: 19 },
  { id: 20, number: 20 },
  { id: 21, number: 21 },
  { id: 22, number: 22 },
  { id: 23, number: 23 },
  { id: 24, number: 24 },
];

type Props = {
  selectedHandover: IHandoverSchema;
  clinicalRecordId: any;
  getAllHandover: (handoverId: number, clinicalId: number) => void;
  finalData: any;
  setHandover: (data: IHandoverSchema) => void;
  clinicalRecord: IClinicalRecordSchema | any;
  onUpdate: () => void;
};

export default function Handover({
  selectedHandover,
  clinicalRecordId,
  getAllHandover,
  finalData,
  setHandover,
  clinicalRecord,
  onUpdate,
}: Props) {
  const lock = useClinicalRecordLock();
  const [outcome, setOutcome] = useState(selectedHandover?.outcome);
  const [recommendations, setRecommendations] = useState<any>([]);
  const [examType, setExamType] = useState<any>([]);
  const [furtherRecommendations, setFurtherRecommendations] = useState<any>([]);
  const [recommendationsOptions, setRecommendationsOptions] = useState<
    string[]
  >([]);
  const [examTypeOptions, setExamTypeOptions] = useState<string[]>([]);
  const [furtherRecommendationsOptions, setFurtherRecommendationsOptions] =
    useState<string[]>([]);
  const [eyeExamRecallPeriod, setEyeExamRecallPeriod] = React.useState('');
  const [openPreviousRecModal, setOpenPreviousRecModal] = useState(false);
  const [referred, setReferred] = useState(false);
  const [isSignatureDialogOpen, setIsSignatureDialogOpen] = useState(false);
  const [isSigning, setIsSigning] = useState(false);
  const [isSavingHandover, setIsSavingHandover] = useState(false);
  const [pendingData, setPendingData] = useState<IHandoverSchema | null>(null);
  const [directTillOpen, setDirectTillOpen] = useState(false);

  /**
   * The refraction behind this handover, in the per-eye shape the voucher quote
   * needs. Passing it lets the SERVER derive the voucher band from the same
   * powers the eventual GOS3 claim will state.
   */
  const handoverPrescription = React.useMemo(
    () => readPrescription(finalData as Record<string, unknown> | undefined),
    [finalData],
  );

  const { patientId } = useParams();
  const navigate = useNavigate();

  const exitToPatientScreen = () => {
    navigate(PATH_DASHBOARD.patients.list);
  };

  const methods = useForm<IHandoverSchema>({});
  const { handleSubmit, reset } = methods;

  const isHandoverAlreadyCreated = !!clinicalRecord?.handoverId;

  const hasClinicalData = !!(
    clinicalRecord?.prescreeningId ||
    clinicalRecord?.refractionId ||
    clinicalRecord?.examinationId ||
    clinicalRecord?.symptomsId
  );

  const handleEyeExamPeriodMonthsChange = (
    event: SelectChangeEvent<string | number>,
  ) => {
    setEyeExamRecallPeriod(String(event.target.value));
  };

  const handleRecommendationsInputChange = (
    event: React.ChangeEvent<{}>,
    value: string,
  ) => {
    if (
      !recommendationsOptions.some(
        (item: any) => item.toLowerCase() === value.toLowerCase(),
      )
    ) {
      setRecommendationsOptions((prevRecommendations: any) => [
        ...prevRecommendations,
        value.trim(),
      ]);
    }
  };

  const handleFurtherRecommendationsInputChange = (
    event: React.ChangeEvent<{}>,
    value: string,
  ) => {
    if (
      !furtherRecommendationsOptions.some(
        (item: any) => item.toLowerCase() === value.toLowerCase(),
      )
    ) {
      setFurtherRecommendationsOptions((prevRecommendations: any) => [
        ...prevRecommendations,
        value.trim(),
      ]);
    }
  };

  const handleExamTypeInputChange = (
    event: React.ChangeEvent<{}>,
    value: string,
  ) => {
    if (
      !examTypeOptions.some(
        (item: any) => item.toLowerCase() === value.toLowerCase(),
      )
    ) {
      setExamTypeOptions((prevRecommendations: any) => [
        ...prevRecommendations,
        value.trim(),
      ]);
    }
  };

  const handleChange = (event: SelectChangeEvent) => {
    setOutcome(event.target.value as string);
  };

  const recommendationsData = [
    { label: 'Distance Single Vision' },
    { label: 'Intermediate Single Vision' },
    { label: 'Near Single Vision' },
    { label: 'Bifocals' },
    { label: 'Varifocals' },
    { label: 'Occupational' },
    { label: 'Prescription Sunglasses' },
    { label: 'Contact Lenses' },
    ...recommendationsOptions.map((item) => ({ label: item })),
  ];

  const examTypeData = [
    { label: 'Private Eye Exam' },
    { label: 'NHS Exam' },
    { label: 'Contact Lens Fit' },
    { label: 'Contact Lens AfterCare' },
    { label: 'Staff Eye Exam' },
    { label: 'Safety Eye Exam' },
    { label: 'Re-Check' },
    { label: 'Other' },
    ...examTypeOptions.map((item) => ({ label: item })),
  ];

  const furtherRecommendationsData = [
    { label: 'MAR' },
    { label: '1.6' },
    { label: '1.67' },
    { label: '1.74' },
    { label: 'Blue Protect' },
    { label: 'Tint' },
    ...furtherRecommendationsOptions.map((item) => ({ label: item })),
  ];

  const recommendationsArray =
    selectedHandover?.recommendations
      ?.split(',')
      .map((recommendation: any) => ({ label: recommendation })) || [];

  const examTypeArray =
    selectedHandover?.examType
      ?.split(',')
      .map((examType: any) => ({ label: examType })) || [];

  const furtherRecommendationsArray = selectedHandover?.furtherRecommendations
    ? selectedHandover.furtherRecommendations
        ?.split(',')
        .map((furtherRecommendations: any) => ({
          label: furtherRecommendations.trim(),
        }))
    : [];

  const performHandover = async (data: IHandoverSchema) => {
    if (isSavingHandover) return;
    const missingFields: string[] = [];
    if (!recommendations?.length) missingFields.push('Recommendations');
    if (!examType?.length) missingFields.push('Exam Type');
    if (!outcome) missingFields.push('Outcome');

    if (missingFields.length > 0) {
      enqueueSnackbar(`Missing required fields: ${missingFields.join(', ')}`, {
        variant: 'error',
      });
      return;
    }

    setIsSavingHandover(true);
    try {
      const recommendationsDataParsed = recommendations
        .map((item: any) => item.label)
        .join(', ');
      const examTypeDataParsed = examType
        .map((item: any) => item.label)
        .join(', ');
      const furtherRecommendationsDataParsed = furtherRecommendations
        .map((item: any) => item.label)
        .join(', ');

      const handoverData = {
        ...data,
        recommendations: recommendationsDataParsed,
        examType: examTypeDataParsed,
        furtherRecommendations: furtherRecommendationsDataParsed,
        outcome,
        referred,
        eyeExamRecallPeriod,
        clinicalRecordId,
      };
      // Amending a signed record: stage the change instead of saving it.
      // No new dispense is created; the recall follows the amended values.
      if (
        lock.stageIfAmending('handover', {
          ...handoverData,
          clinicalRecordId,
          patientId,
        } as Record<string, unknown>)
      ) {
        setIsSavingHandover(false);
        return;
      }

      const handoverService = new HandoverService();
      const handoverResponse = await handoverService.createHandover(
        patientId!,
        {
          ...handoverData,
          clinicalRecordId,
        },
      );
      const handoverId = handoverResponse.data.id;
      const clinicalId = handoverResponse.data.clinicalRecordId;
      const dispenseId = handoverResponse.data.dispense?.id;

      getAllHandover(handoverId, clinicalId);
      onUpdate();
      notifyHandoverSaveResult(
        enqueueSnackbar,
        'Handover and Respective Dispense Created Successfully!',
        handoverResponse.data?.recallWarning,
      );
      if (dispenseId) {
        navigate(`/dispense/${dispenseId}`);
      }
    } catch (error: unknown) {
      enqueueSnackbar(
        getApiErrorMessage(
          error,
          'Failed to save handover. Please check required fields and try again.',
        ),
        { variant: 'error' },
      );
    } finally {
      setIsSavingHandover(false);
    }
  };

  const handleSignOff = async (signatureData: string) => {
    if (!clinicalRecordId) return;
    setIsSigning(true);
    try {
      const service = new ClinicalRecords();
      await service.signOff(clinicalRecordId, signatureData);
      enqueueSnackbar('Clinical record signed off successfully', {
        variant: 'success',
      });
      setIsSignatureDialogOpen(false);

      // PROCEED WITH HANDOVER AFTER SIGN-OFF
      if (pendingData) {
        await performHandover(pendingData);
        setPendingData(null);
      } else {
        onUpdate && onUpdate();
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Failed to sign off clinical record', {
        variant: 'error',
      });
    } finally {
      setIsSigning(false);
    }
  };

  const onSubmit = async (data: IHandoverSchema) => {
    if (isHandoverAlreadyCreated) {
      enqueueSnackbar('Handover is already created for this clinical record.', {
        variant: 'info',
      });
      return;
    }

    if (isSavingHandover || isSigning) return;
    if (hasClinicalData && !clinicalRecord?.signedAt) {
      setIsSignatureDialogOpen(true);
      setPendingData(data);
    } else if (hasClinicalData && clinicalRecord?.signedAt) {
      await performHandover(data);
    } else {
      alert('Clinical Record must be signed off before handover.');
    }
  };

  const setHandoverData = () => {
    if (selectedHandover && Object.keys(selectedHandover).length > 0) {
      reset({
        outcome: selectedHandover?.outcome || '',
        notes: selectedHandover?.notes || '',
        examiner: toUpperText(selectedHandover?.examiner || user?.username),
        referred: selectedHandover?.referred,
        eyeExamRecallPeriod: selectedHandover?.eyeExamRecallPeriod || '',
      });

      setRecommendations(
        selectedHandover.recommendations
          ? selectedHandover.recommendations
              .split(',')
              .map((item: any) => ({ label: item }))
          : [],
      );
      setExamType(
        selectedHandover.examType
          ? selectedHandover.examType
              .split(',')
              .map((item: any) => ({ label: item }))
          : [],
      );
      setFurtherRecommendations(
        selectedHandover.furtherRecommendations
          ? selectedHandover.furtherRecommendations
              .split(',')
              .map((item: any) => ({ label: item }))
          : [],
      );
      setOutcome(selectedHandover.outcome || '');
      setReferred(!!selectedHandover.referred);
      setEyeExamRecallPeriod(
        selectedHandover.eyeExamRecallPeriod != null &&
          selectedHandover.eyeExamRecallPeriod !== ''
          ? String(selectedHandover.eyeExamRecallPeriod)
          : '',
      );
    } else {
      reset({
        outcome: '',
        notes: '',
        examiner: toUpperText(user?.username),
        referred: false,
        eyeExamRecallPeriod: '',
      });

      setRecommendations([]);
      setExamType([]);
      setFurtherRecommendations([]);
      setOutcome('');
      setReferred(false);
      setEyeExamRecallPeriod('');
    }
  };

  const { user } = useAuthContext();

  useEffect(() => {
    if (selectedHandover) {
      setHandoverData();
    }
  }, [selectedHandover]);

  return (
    <FormProvider
      methods={methods}
      onSubmit={handleSubmit(onSubmit)}
      key={selectedHandover?.id || 'new-handover'}
    >
      <Box sx={{ ml: '1px', mr: '8px' }}>
        <Stack
          direction="row"
          justifyContent="flex-end"
          sx={{ px: 2, pt: 2, pb: 1 }}
        >
          <Button
            variant="outlined"
            startIcon={<Iconify icon="eva:clock-outline" />}
            onClick={() => setOpenPreviousRecModal(true)}
          >
            Previous Records
          </Button>
        </Stack>

        {!hasClinicalData && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              No clinical data found. Please complete at least one clinical form
              before signing off.
            </Alert>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="center"
              flexWrap="wrap"
            >
              <Button variant="contained" disabled>
                Sign Off Clinical Record
              </Button>
              <Button variant="outlined" disabled>
                Do Handover
              </Button>
              <Button variant="outlined" onClick={exitToPatientScreen}>
                Exit
              </Button>
            </Stack>
          </Box>
        )}

        {/* Clinician sign-off (client bug 39): the state already existed but
            was easy to miss. The banner now shows for every signed record, not
            only Eye Examinations, and the "not yet signed" panel no longer
            offers a second Sign Off button for a record that is already signed
            (signing again is refused by the server). The lock, amendment and
            permission rules behind it are untouched. */}
        {hasClinicalData && clinicalRecord?.signedAt && (
          <SignedOffBanner
            signedBy={clinicalRecord.signedByUser?.username}
            signedAt={clinicalRecord.signedAt}
            sx={{ mx: 2, mb: 2 }}
          />
        )}

        {hasClinicalData && !clinicalRecord?.signedAt && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Clinical record must be signed off before handover. Signing stores
              who signed and when, and locks the record so any later change is
              recorded as an amendment.
            </Alert>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="center"
              flexWrap="wrap"
            >
              <LoadingButton
                variant="contained"
                color="primary"
                startIcon={<Iconify icon="eva:edit-2-outline" />}
                onClick={() => setIsSignatureDialogOpen(true)}
                loading={isSigning}
                disabled={isSigning || isSavingHandover}
              >
                Sign Off Clinical Record
              </LoadingButton>
              <Button
                variant="outlined"
                onClick={exitToPatientScreen}
                disabled={isSigning || isSavingHandover}
              >
                Exit
              </Button>
            </Stack>
          </Box>
        )}

        {hasClinicalData &&
          clinicalRecord?.signedAt &&
          clinicalRecord?.type !== 'Eye Examination' && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                This record is signed off. Handover is completed on the tab for
                this record type, not here.
              </Alert>
              <Button
                variant="outlined"
                onClick={exitToPatientScreen}
                disabled={isSigning || isSavingHandover}
              >
                Exit
              </Button>
            </Box>
          )}

        {hasClinicalData &&
          clinicalRecord?.signedAt &&
          clinicalRecord?.type === 'Eye Examination' && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '98%',
                paddingRight: 3,
                paddingLeft: 2,
              }}
            >
              <FinalSummary finalData={finalData} />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  width: '100%',
                }}
              >
                <Grid item xs={12} md={4} width="50%">
                  <Stack
                    sx={{
                      width: '90%',
                      borderRadius: '15px',
                      boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.16)',
                      maxHeight: '380px',
                      overflowY: 'auto',
                    }}
                    direction="column"
                    mt={2}
                    ml={2}
                    p={2}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant="h6">Recommendations</Typography>
                    </Box>
                    <Box mt={2}>
                      <Stack spacing={3} sx={{ width: 500 }}>
                        <Autocomplete
                          options={recommendationsData}
                          multiple
                          defaultValue={recommendationsArray}
                          getOptionLabel={(option: any) => option?.label}
                          onChange={(event, data) => {
                            setRecommendations(data);
                          }}
                          onInputChange={(event, value) => {
                            handleRecommendationsInputChange(event, value);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="standard"
                              label="Recommendations"
                              inputProps={{
                                ...params.inputProps,
                                required: false,
                              }}
                            />
                          )}
                        />
                      </Stack>
                    </Box>
                  </Stack>
                  <Stack
                    sx={{
                      width: '90%',
                      borderRadius: '15px',
                      boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.16)',
                      maxHeight: '380px',
                      overflowY: 'auto',
                    }}
                    direction="column"
                    mt={2}
                    ml={2}
                    p={2}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant="h6">Exam Type</Typography>
                    </Box>
                    <Box mt={2}>
                      <Stack spacing={3} sx={{ width: 500 }}>
                        <Autocomplete
                          multiple
                          options={examTypeData}
                          defaultValue={examTypeArray}
                          getOptionLabel={(option) => option?.label}
                          onChange={(event, data) => {
                            setExamType(data);
                          }}
                          onInputChange={(event, value) => {
                            handleExamTypeInputChange(event, value);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="standard"
                              label="Exam type"
                              inputProps={{
                                ...params.inputProps,
                                required: false,
                              }}
                            />
                          )}
                        />
                      </Stack>
                    </Box>
                  </Stack>
                  <Stack
                    sx={{
                      width: '90%',
                      borderRadius: '15px',
                      boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.16)',
                      maxHeight: '380px',
                      overflowY: 'auto',
                    }}
                    direction="column"
                    mt={2}
                    ml={2}
                    p={2}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant="h6">Outcome</Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                        <Typography variant="body1" sx={{ mt: 1 }}>
                          Referred
                        </Typography>
                        {/* `checked` used to read the immutable prop while
                            `onChange` wrote to local state, so the box never
                            moved (client bug 37). Both sides now use the state,
                            which `setHandoverData` seeds from the record. */}
                        <Checkbox
                          {...label}
                          checked={referred}
                          onChange={(e) => setReferred(e.target.checked)}
                        />
                      </Box>
                    </Box>
                    <Box sx={{ minWidth: 120, mt: 3 }}>
                      <FormControl fullWidth>
                        <InputLabel id="handover-outcome-label">
                          Outcome
                        </InputLabel>
                        <Select
                          labelId="handover-outcome-label"
                          id="handover-outcome"
                          value={outcome ?? ''}
                          label="Outcome"
                          onChange={handleChange}
                        >
                          <MenuItem value={'NewRx'}>New Rx</MenuItem>
                          <MenuItem value={'NoChange'}>No Change</MenuItem>
                          <MenuItem value={'NoRx'}>No Rx</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Stack>
                  <Stack
                    sx={{
                      width: '90%',
                      borderRadius: '15px',
                      boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.16)',
                      maxHeight: '380px',
                      overflowY: 'auto',
                      alignItems: 'center',
                    }}
                    direction="row"
                    mt={2}
                    ml={2}
                    p={2}
                  >
                    <Typography
                      variant="h6"
                      sx={{ minWidth: 220, textAlign: 'left' }}
                    >
                      Eye Exam Recall Period
                    </Typography>
                    <FormControl sx={{ ml: 2, width: '25%', mr: 2 }}>
                      <InputLabel id="handover-recall-months-label">
                        Months
                      </InputLabel>
                      <Select
                        labelId="handover-recall-months-label"
                        id="handover-recall-months"
                        label="Months"
                        value={
                          eyeExamRecallPeriod === ''
                            ? ''
                            : Number(eyeExamRecallPeriod)
                        }
                        onChange={handleEyeExamPeriodMonthsChange}
                      >
                        {monthsData.map((months) => {
                          return (
                            <MenuItem key={months.id} value={months.number}>
                              {months.number}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={4} width="50%">
                  <Stack
                    sx={{
                      width: '90%',
                      borderRadius: '15px',
                      boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.16)',
                      maxHeight: '380px',
                      overflowY: 'auto',
                    }}
                    direction="column"
                    mt={2}
                    ml={2}
                    p={2}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant="h6">
                        Further Recommendations
                      </Typography>
                    </Box>
                    <Box mt={2}>
                      <Stack spacing={3} sx={{ width: 500 }}>
                        <Autocomplete
                          multiple
                          options={furtherRecommendationsData}
                          defaultValue={furtherRecommendationsArray}
                          getOptionLabel={(option) => option?.label}
                          onChange={(event, data) => {
                            setFurtherRecommendations(data);
                          }}
                          onInputChange={(event, value) => {
                            handleFurtherRecommendationsInputChange(
                              event,
                              value,
                            );
                          }}
                          renderOption={(props, option) => (
                            <li {...props}>{option.label}</li>
                          )}
                          renderInput={(params) => (
                            <RHFTextField
                              {...params}
                              variant="standard"
                              name="furtherRecommendations"
                            />
                          )}
                        />
                      </Stack>
                    </Box>
                  </Stack>
                  <Stack
                    sx={{
                      width: '90%',
                      borderRadius: '15px',
                      boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.16)',
                      maxHeight: '380px',
                      overflowY: 'auto',
                    }}
                    direction="column"
                    mt={2}
                    ml={2}
                    p={2}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant="h6">Notes</Typography>
                    </Box>
                    <Box mt={2}>
                      <RHFTextField
                        placeholder="Please Add Notes"
                        multiline
                        minRows={2}
                        maxRows={10}
                        name="notes"
                        inputProps={{ maxLength: 50000 }} // matches the API limit
                      />
                    </Box>
                  </Stack>
                  <Stack
                    sx={{
                      width: '90%',
                      borderRadius: '15px',
                      boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.16)',
                      maxHeight: '380px',
                      overflowY: 'auto',
                    }}
                    direction="column"
                    mt={2}
                    ml={2}
                    p={2}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant="h6">Examiner</Typography>
                    </Box>
                    <Box mt={2}>
                      <RHFTextField
                        value={toUpperText(user?.username)}
                        multiline
                        rows={2}
                        name="examiner"
                        uppercase
                      />
                    </Box>
                  </Stack>
                </Grid>
              </Box>
              <Stack
                direction="row-reverse"
                mt="40px"
                gap="30px"
                mr="17px"
                mb="20px"
              >
                <LoadingButton
                  type="button"
                  variant="contained"
                  onClick={exitToPatientScreen}
                  disabled={isSavingHandover || isSigning}
                >
                  Exit
                </LoadingButton>
                <LoadingButton
                  type="submit"
                  variant="outlined"
                  loading={isSavingHandover}
                  disabled={
                    isHandoverAlreadyCreated || isSavingHandover || isSigning
                  }
                >
                  {isHandoverAlreadyCreated
                    ? 'Handover Already Created'
                    : 'Do Handover'}
                </LoadingButton>
                {/* NHS voucher supplies and chargeable exams are paid for while
                  the patient is still at the desk — so the till is raised here
                  rather than after a detour through the Dispense screen. */}
                {selectedHandover?.id && (
                  <Button
                    type="button"
                    variant="outlined"
                    color="success"
                    startIcon={<Iconify icon="mdi:cash-register" />}
                    onClick={() => setDirectTillOpen(true)}
                    disabled={isSavingHandover || isSigning}
                  >
                    Direct Till
                  </Button>
                )}
              </Stack>
              {isHandoverAlreadyCreated && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: 'right', mr: '17px', mb: '20px' }}
                >
                  This clinical record is already handed over.
                </Typography>
              )}
            </Box>
          )}
      </Box>
      <Dialog
        fullWidth
        maxWidth="md"
        open={openPreviousRecModal}
        onClose={() => setOpenPreviousRecModal(false)}
      >
        <DialogTitle>Previous Records</DialogTitle>
        <PreviousRecords
          selectedRow={clinicalRecordId}
          setPreviousRecordData={setHandover}
          tab="handover"
        />
      </Dialog>
      <SignatureDialog
        open={isSignatureDialogOpen}
        onClose={() => setIsSignatureDialogOpen(false)}
        onConfirm={handleSignOff}
        loading={isSigning}
      />
      {selectedHandover?.id && patientId && (
        <DirectTillDialog
          open={directTillOpen}
          onClose={() => setDirectTillOpen(false)}
          patientId={patientId}
          handoverId={Number(selectedHandover.id)}
          examType={selectedHandover.examType}
          prescription={handoverPrescription}
          onCreated={(result) => {
            if (result.tillId) {
              navigate(`/till/${result.tillId}`, {
                state: buildTillFocusState(result.dispenseId),
              });
            }
          }}
        />
      )}
    </FormProvider>
  );
}
